export type MeasurementSystem = 'metric' | 'imperial';

const KM_PER_MILE = 1.609344;

/** Maximum shipping zone radius persisted in the database (kilometres). */
export const MAX_SHIPPING_RADIUS_KM = 9999;

const MEASUREMENT_SYSTEMS: readonly MeasurementSystem[] = [
    'metric',
    'imperial',
];

export const isMeasurementSystem = (
    value: string,
): value is MeasurementSystem =>
    (MEASUREMENT_SYSTEMS as readonly string[]).includes(value);

/**
 * Great-circle distance between two WGS84 points, in canonical kilometres.
 */
export const coordinateDistanceKm = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
): number => {
    const radLat1 = (Math.PI * lat1) / 180;
    const radLat2 = (Math.PI * lat2) / 180;
    const theta = lon1 - lon2;
    const radTheta = (Math.PI * theta) / 180;
    let dist =
        Math.sin(radLat1) * Math.sin(radLat2) +
        Math.cos(radLat1) * Math.cos(radLat2) * Math.cos(radTheta);
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    return dist * KM_PER_MILE;
};

export interface FormatDistanceOptions {
    measurementSystem: MeasurementSystem;
    locale?: string;
}

/**
 * Formats a canonical kilometre distance for the active market measurement system.
 */
export const formatDistanceFromKm = (
    distanceKm: number,
    { measurementSystem, locale = 'en' }: FormatDistanceOptions,
): string => {
    if (measurementSystem === 'imperial') {
        const miles = distanceKm / KM_PER_MILE;
        return new Intl.NumberFormat(locale, {
            style: 'unit',
            unit: 'mile',
            unitDisplay: 'short',
            maximumFractionDigits: miles < 10 ? 1 : 0,
        }).format(miles);
    }

    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'short',
        maximumFractionDigits: distanceKm < 10 ? 1 : 0,
    }).format(distanceKm);
};

/**
 * Converts a stored shipping radius (km) to the unit shown in BM inputs.
 */
export const radiusKmToDisplayValue = (
    radiusKm: number,
    measurementSystem: MeasurementSystem,
): number => {
    if (measurementSystem === 'imperial') {
        return Math.round((radiusKm / KM_PER_MILE) * 10) / 10;
    }

    return radiusKm;
};

/**
 * Converts a BM radius input value back to canonical kilometres for persistence.
 */
export const displayRadiusToKm = (
    displayValue: number,
    measurementSystem: MeasurementSystem,
): number => {
    let radiusKm = displayValue;
    if (measurementSystem === 'imperial') {
        radiusKm = Math.round(displayValue * KM_PER_MILE);
    } else {
        radiusKm = Math.round(displayValue);
    }

    return Math.min(
        MAX_SHIPPING_RADIUS_KM,
        Math.max(0, radiusKm),
    );
};

/** Maximum radius value shown in BM inputs for the active measurement system. */
export const maxDisplayRadius = (
    measurementSystem: MeasurementSystem,
): number =>
    radiusKmToDisplayValue(MAX_SHIPPING_RADIUS_KM, measurementSystem);

/** Short unit label for radius form fields. */
export const radiusUnitLabel = (measurementSystem: MeasurementSystem): string =>
    measurementSystem === 'imperial' ? 'mi' : 'km';

/**
 * Reads the market measurement system injected on legacy BM pages.
 */
export const getWindowMeasurementSystem = (): MeasurementSystem => {
    if (typeof window === 'undefined') {
        return 'metric';
    }

    const raw =
        (window as Window & { MEASUREMENT_SYSTEM?: string })
            .MEASUREMENT_SYSTEM ?? '';

    return isMeasurementSystem(raw) ? raw : 'metric';
};
